import { useMemo, useState } from "react";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { enterExplicitFocus } from "@/lib/focus-presentation";
import {
  archiveFrames,
  copyFrames,
  deleteFrames,
  unarchiveFrame,
} from "@/lib/frame-menu-actions";
import { useSelectionStore } from "@/stores/selection-store";
import { selectionMenuGroups } from "./card-menu-items";
import { WallCardMenu, type WallCardMenuAction } from "./wall-card-menu";

export interface WallMultiSelectionMenuTarget {
  /** The selection when the menu opened; every item acts on these. */
  frameIds: readonly string[];
  anchor: ContextMenuAnchor;
}

interface WallMultiSelectionMenuProps {
  /** `null` closes it; stay mounted, Delete's confirmation outlives it. */
  target: WallMultiSelectionMenuTarget | null;
  onClose: () => void;
}

function runSelectionAction(
  frameIds: readonly string[],
  action: WallCardMenuAction,
): void {
  switch (action) {
    case "focus":
      // The core tiles up to four.
      enterExplicitFocus(frameIds);
      return;
    case "copy":
      void copyFrames(frameIds);
      return;
    case "archive":
      void archiveFrames(frameIds);
      return;
    case "unarchive":
      for (const frameId of frameIds) void unarchiveFrame(frameId);
      return;
    case "delete":
      void deleteFrames(frameIds);
      useSelectionStore.getState().clearMulti();
      return;
  }
}

/**
 * The menu of a selected card while several are selected (Part 05 Q6), from
 * the shared model: Focus these windows │ Copy │ Move to project… ·
 * Archive (Unarchive in Archived) │ Delete.
 */
export function WallMultiSelectionMenu({
  target,
  onClose,
}: WallMultiSelectionMenuProps) {
  // The groups as the menu opened; it keeps them while it fades out.
  const [shown, setShown] = useState(target);
  if (target && target !== shown) setShown(target);
  const groups = useMemo(
    () => selectionMenuGroups(shown?.frameIds ?? []),
    [shown],
  );
  return (
    <WallCardMenu
      target={target}
      groups={groups}
      onClose={onClose}
      onAction={runSelectionAction}
    />
  );
}
